import { normalizeStaticFullscreenType } from "Editor/utils/fullscreenType";
import React from "react";
import { Sparkles } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import {
  DotsHorizontalIcon,
  Link2Icon,
  TrashIcon,
  Pencil1Icon,
  BarChartIcon,
  CopyIcon,
} from "@radix-ui/react-icons";
import { useSWRConfig } from "swr";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import {
  MyFormInput,
  MyFormSelect,
  MyFromTextarea,
} from "components/ui/MyFormField";
import { ErrorsNote } from "components/template-data-table-column";
import { delFetcher, postFetcher, useAuthStore } from "api/swr";
import { displayNameFromFields } from "commons/userDisplayName";
import Loader from "@/components/ui/Loader";
import useSWRMutation from "swr/mutation";

export function DataTableRowActions({ row }) {
  const data = row.original;
  const navigate = useNavigate();

  return (
    <>
      <div className="flex gap-3 items-center">
        <Button asChild>
          <Link to={"/editor/" + row.original.id}>
            <Pencil1Icon className="h-4 w-4 mr-2" /> Paint now
          </Link>
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="flex h-8 w-8 p-0 data-[state=open]:bg-muted"
            >
              <DotsHorizontalIcon className="h-4 w-4" />
              <span className="sr-only">Open menu</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[160px]">
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuItem
              onSelect={() =>
                window.open(`/admin/avatars?tid=${data.id}`, "_blank")
              }
            >
              <Link2Icon className="w-4 h-4 mr-2" />
              Avatars
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() =>
                navigate(`/admin/frames/${data.id}/report-summary`)
              }
            >
              <BarChartIcon className="w-4 h-4 mr-2" />
              Report
            </DropdownMenuItem>
            <DataTableRowCopy row={row} />
            <DropdownMenuItem asChild>
              <a
                href={`/admin/ops-tools?opsToolsTab=template-tag-recommend&tid=${encodeURIComponent(data.id)}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Sparkles className="mr-2 h-4 w-4 shrink-0 fill-amber-300 text-fuchsia-500" />
                AI 打标签
              </a>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DataTableRowDelete row={row} />
          </DropdownMenuContent>
        </DropdownMenu>
        <ErrorsNote row={row} />
      </div>
    </>
  );
}

export const DataTableRowDelete = ({ row }) => {
  const data = row.original;
  const { toast } = useToast();
  const { trigger, isMutating } = useSWRMutation("/templates", delFetcher, {
    onSuccess: (res) => {
      if (res.code === 403) {
        toast({
          title: "Error",
          description: res.message,
          variant: "destructive",
        });
        return;
      }
      toast({
        title: "Template Deleted",
        description: "The Template has been successfully deleted.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });
  const [open, setOpen] = React.useState(false);
  const handleDelete = async () => {
    await trigger({ id: data.id });
    setOpen(false);
  };
  const openDialog = (e) => {
    e.preventDefault();
    setOpen(true);
  };

  return (
    <>
      <DropdownMenuItem
        className="text-destructive focus:bg-destructive focus:text-destructive-foreground"
        onSelect={openDialog}
      >
        <TrashIcon className="mr-2 h-4 w-4" />
        Delete{isMutating && "ing..."}
        <DropdownMenuShortcut>⌘⌫</DropdownMenuShortcut>
      </DropdownMenuItem>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Are you sure you want to delete this template?
            </DialogTitle>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={isMutating}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={isMutating}
            >
              {isMutating ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

const copyFormSchema = z.object({
  name: z
    .string()
    .min(2, { message: "name must be at least 2 characters." })
    .max(80, { message: "name must not be longer than 80 characters." }),
  intro: z.string().max(640).optional(),
  fullscreenType: z.preprocess(
    (val) => Number(val),
    z.number().int().refine((v) => [0, 2, 3].includes(v)),
  ),
});

export const DataTableRowCopy = ({ row }) => {
  const data = row.original;
  const { toast } = useToast();
  const { mutate } = useSWRConfig();
  const user = useAuthStore((state) => state.auth?.user);
  const [open, setOpen] = React.useState(false);
  const [copying, setCopying] = React.useState(false);

  const publisher = displayNameFromFields(user) || user?.id || "user";
  const sourceFullscreenType = normalizeStaticFullscreenType(data.fullscreenType);
  // 3:4 画布不是方形，组件坐标不能按单一比例换算，只能复制成 3:4
  const fullscreenTypeItems =
    sourceFullscreenType === 3
      ? [{ code: "3", name: "3:4" }]
      : [
          { code: "0", name: "1:1（普通）" },
          { code: "2", name: "9:16（全屏）" },
        ];

  const form = useForm({
    resolver: zodResolver(copyFormSchema),
    defaultValues: {
      name: `${data.name || "Template"} COPY BY ${publisher}`,
      intro: data.intro || "",
      fullscreenType: String(sourceFullscreenType),
    },
  });

  const watchedFullscreenType = Number(form.watch("fullscreenType"));
  const needRebuild = watchedFullscreenType !== sourceFullscreenType;

  const resetDialogState = () => {
    setCopying(false);
    form.reset({
      name: `${data.name || "Template"} COPY BY ${publisher}`,
      intro: data.intro || "",
      fullscreenType: String(sourceFullscreenType),
    });
  };

  const openDialog = (e) => {
    e.preventDefault();
    resetDialogState();
    setOpen(true);
  };

  const handleOpenChange = (next) => {
    if (copying) return;
    setOpen(next);
    if (!next) resetDialogState();
  };

  const onSubmit = async (values) => {
    if (copying) return;
    setCopying(true);
    try {
      const res = await postFetcher(`/templates/copy/${data.id}`, {
        arg: {
          name: values.name,
          intro: values.intro || "",
          fullscreenType: Number(values.fullscreenType),
        },
      });
      if (!res?.id) {
        throw new Error(res?.message || "Failed to copy template");
      }
      const copied = Number(res?.copiedAvatars || 0);
      const failed = Number(res?.failedAvatars || 0);
      const avatarHint =
        copied > 0
          ? `已带上 ${copied} 个 Avatar${failed > 0 ? `，${failed} 个失败` : ""}。普通 Avatar 可能仍在生图。`
          : "源模版没有可复制的后台 Avatar。";
      toast({
        title: "Template Copied",
        description: `模版复制成功。${avatarHint}`,
        className: "bg-green-500 text-white",
      });
      mutate(
        (key) => Array.isArray(key) && key[0] === "/templates",
        undefined,
        { revalidate: true },
      );
      setOpen(false);
      resetDialogState();
    } catch (error) {
      setCopying(false);
      toast({
        title: "Error",
        description: error.message || "Failed to copy template",
        variant: "destructive",
      });
    }
  };

  return (
    <>
      <DropdownMenuItem onSelect={openDialog}>
        <CopyIcon className="mr-2 h-4 w-4" />
        Copy
      </DropdownMenuItem>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="dialog-content sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Copy Template</DialogTitle>
            <DialogDescription>
              可修改名称、描述与画幅比例。会同时带上该模版最近 10 个后台创建的
              Avatar（含混合 Spine）。切换画幅时会从 original 重建组件图，耗时更长。
            </DialogDescription>
          </DialogHeader>

          {copying ? (
            <div className="flex items-center gap-3 py-6">
              <Loader className="h-6 w-6 text-primary" />
              <div className="min-w-0">
                <p className="text-sm font-medium">正在复制模版和 Avatar…</p>
                <p className="text-sm text-muted-foreground">
                  {needRebuild
                    ? "画幅转换会从 original 原图重新截取组件，并带上最近 10 个后台 Avatar，请耐心等待，不要关闭页面。"
                    : "同时复制最近 10 个后台 Avatar，请耐心等待，不要关闭页面。"}
                </p>
              </div>
            </div>
          ) : (
            <Form {...form}>
              <form
                id="copy-template-form"
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-4"
              >
                <MyFormInput
                  name="name"
                  form={form}
                  label="Name"
                  placeholder="template name"
                />
                <MyFromTextarea
                  name="intro"
                  form={form}
                  label="Description"
                  placeholder="template description"
                />
                <MyFormSelect
                  name="fullscreenType"
                  form={form}
                  label="画幅比例"
                  items={fullscreenTypeItems}
                  placeholder="画幅比例"
                />
                {needRebuild && (
                  <p className="text-sm text-amber-600">
                    将从 {sourceFullscreenType === 2 ? "9:16" : "1:1"} 转为{" "}
                    {watchedFullscreenType === 2 ? "9:16" : "1:1"}
                    ，会从 original 原图按新画幅重新截取组件图，组件较多时请耐心等待。
                  </p>
                )}
              </form>
            </Form>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={copying}
            >
              Cancel
            </Button>
            {!copying && (
              <Button type="submit" form="copy-template-form">
                Confirm Copy
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
